import { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  Save, Download, Trash2, Plus, Image, Type, QrCode, 
  Square, Minus, Palette, Eye, Layers, 
  AlignLeft, AlignCenter, AlignRight, Bold, Italic,
  ChevronUp, ChevronDown, RotateCcw, Lock, Unlock,
  Upload, User, GraduationCap, Briefcase, ImagePlus,
  ArrowLeft
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const getAuthHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

// Card orientations
const ORIENTATIONS = {
  LANDSCAPE: { width: 340, height: 215, label: 'Landscape (Standard)' },
  PORTRAIT: { width: 215, height: 340, label: 'Portrait (Vertical)' }
};

// Card types
const CARD_TYPES = {
  STUDENT: 'student',
  TEACHER: 'teacher',
  STAFF: 'staff'
};

// Element types
const ELEMENT_TYPES = {
  TEXT: 'text',
  IMAGE: 'image',
  LOGO: 'logo',
  PHOTO: 'photo',
  QR: 'qr',
  SHAPE: 'shape',
  LINE: 'line'
};

// Default student card elements - Landscape
const DEFAULT_STUDENT_LANDSCAPE = [
  { id: 'logo', type: ELEMENT_TYPES.LOGO, x: 10, y: 10, width: 50, height: 50, locked: false },
  { id: 'school_name', type: ELEMENT_TYPES.TEXT, x: 70, y: 15, width: 200, height: 30, text: '{{school_name}}', fontSize: 14, fontWeight: 'bold', color: '#1e40af', align: 'left' },
  { id: 'card_title', type: ELEMENT_TYPES.TEXT, x: 70, y: 40, width: 100, height: 20, text: 'STUDENT ID CARD', fontSize: 10, fontWeight: 'bold', color: '#374151', align: 'left' },
  { id: 'photo', type: ELEMENT_TYPES.PHOTO, x: 15, y: 70, width: 70, height: 85, locked: false },
  { id: 'name_label', type: ELEMENT_TYPES.TEXT, x: 95, y: 70, width: 50, height: 15, text: 'Name:', fontSize: 9, color: '#6b7280' },
  { id: 'name', type: ELEMENT_TYPES.TEXT, x: 145, y: 70, width: 120, height: 15, text: '{{name}}', fontSize: 10, fontWeight: 'bold', color: '#111827' },
  { id: 'class_label', type: ELEMENT_TYPES.TEXT, x: 95, y: 88, width: 50, height: 15, text: 'Class:', fontSize: 9, color: '#6b7280' },
  { id: 'class', type: ELEMENT_TYPES.TEXT, x: 145, y: 88, width: 120, height: 15, text: '{{class}} - {{section}}', fontSize: 10, color: '#111827' },
  { id: 'id_label', type: ELEMENT_TYPES.TEXT, x: 95, y: 106, width: 50, height: 15, text: 'ID:', fontSize: 9, color: '#6b7280' },
  { id: 'id', type: ELEMENT_TYPES.TEXT, x: 145, y: 106, width: 120, height: 15, text: '{{student_id}}', fontSize: 10, color: '#111827' },
  { id: 'dob_label', type: ELEMENT_TYPES.TEXT, x: 95, y: 124, width: 50, height: 15, text: 'DOB:', fontSize: 9, color: '#6b7280' },
  { id: 'dob', type: ELEMENT_TYPES.TEXT, x: 145, y: 124, width: 120, height: 15, text: '{{dob}}', fontSize: 10, color: '#111827' },
  { id: 'blood_label', type: ELEMENT_TYPES.TEXT, x: 95, y: 142, width: 50, height: 15, text: 'Blood:', fontSize: 9, color: '#6b7280' },
  { id: 'blood', type: ELEMENT_TYPES.TEXT, x: 145, y: 142, width: 50, height: 15, text: '{{blood_group}}', fontSize: 10, fontWeight: 'bold', color: '#dc2626' },
  { id: 'qr', type: ELEMENT_TYPES.QR, x: 275, y: 70, width: 55, height: 55, locked: false },
  { id: 'valid_label', type: ELEMENT_TYPES.TEXT, x: 270, y: 130, width: 60, height: 12, text: 'Valid Till:', fontSize: 8, color: '#6b7280', align: 'center' },
  { id: 'valid', type: ELEMENT_TYPES.TEXT, x: 270, y: 142, width: 60, height: 12, text: '{{valid_till}}', fontSize: 8, color: '#111827', align: 'center' },
  { id: 'footer_line', type: ELEMENT_TYPES.LINE, x: 10, y: 165, width: 320, height: 2, color: '#3b82f6' },
  { id: 'emergency', type: ELEMENT_TYPES.TEXT, x: 10, y: 175, width: 320, height: 15, text: 'Emergency: {{emergency_contact}}', fontSize: 8, color: '#374151', align: 'center' },
  { id: 'address', type: ELEMENT_TYPES.TEXT, x: 10, y: 190, width: 320, height: 20, text: '{{school_address}}', fontSize: 7, color: '#6b7280', align: 'center' }
];

// Default student card elements - Portrait
const DEFAULT_STUDENT_PORTRAIT = [
  { id: 'header_bg', type: ELEMENT_TYPES.SHAPE, x: 0, y: 0, width: 215, height: 70, color: '#1e40af', fill: true },
  { id: 'logo', type: ELEMENT_TYPES.LOGO, x: 82, y: 8, width: 50, height: 50, locked: false },
  { id: 'school_name', type: ELEMENT_TYPES.TEXT, x: 10, y: 60, width: 195, height: 20, text: '{{school_name}}', fontSize: 10, fontWeight: 'bold', color: '#ffffff', align: 'center' },
  { id: 'card_title', type: ELEMENT_TYPES.TEXT, x: 10, y: 78, width: 195, height: 15, text: 'STUDENT IDENTITY CARD', fontSize: 9, fontWeight: 'bold', color: '#1e40af', align: 'center' },
  { id: 'photo', type: ELEMENT_TYPES.PHOTO, x: 57, y: 95, width: 100, height: 120, locked: false },
  { id: 'name', type: ELEMENT_TYPES.TEXT, x: 10, y: 220, width: 195, height: 18, text: '{{name}}', fontSize: 12, fontWeight: 'bold', color: '#111827', align: 'center' },
  { id: 'class', type: ELEMENT_TYPES.TEXT, x: 10, y: 238, width: 195, height: 15, text: 'Class: {{class}} - {{section}}', fontSize: 10, color: '#374151', align: 'center' },
  { id: 'id', type: ELEMENT_TYPES.TEXT, x: 10, y: 255, width: 195, height: 15, text: 'ID: {{student_id}}', fontSize: 10, color: '#374151', align: 'center' },
  { id: 'dob', type: ELEMENT_TYPES.TEXT, x: 10, y: 272, width: 100, height: 15, text: 'DOB: {{dob}}', fontSize: 9, color: '#6b7280', align: 'left' },
  { id: 'blood', type: ELEMENT_TYPES.TEXT, x: 105, y: 272, width: 100, height: 15, text: 'Blood: {{blood_group}}', fontSize: 9, fontWeight: 'bold', color: '#dc2626', align: 'right' },
  { id: 'qr', type: ELEMENT_TYPES.QR, x: 77, y: 290, width: 60, height: 60, locked: false },
  { id: 'footer_line', type: ELEMENT_TYPES.LINE, x: 10, y: 355, width: 195, height: 2, color: '#3b82f6' },
  { id: 'emergency', type: ELEMENT_TYPES.TEXT, x: 10, y: 360, width: 195, height: 12, text: 'Emergency: {{emergency_contact}}', fontSize: 7, color: '#374151', align: 'center' },
  { id: 'address', type: ELEMENT_TYPES.TEXT, x: 10, y: 372, width: 195, height: 20, text: '{{school_address}}', fontSize: 6, color: '#6b7280', align: 'center' }
];

// Default teacher card elements - Landscape
const DEFAULT_TEACHER_LANDSCAPE = [
  { id: 'logo', type: ELEMENT_TYPES.LOGO, x: 10, y: 10, width: 50, height: 50, locked: false },
  { id: 'school_name', type: ELEMENT_TYPES.TEXT, x: 70, y: 15, width: 200, height: 30, text: '{{school_name}}', fontSize: 14, fontWeight: 'bold', color: '#059669', align: 'left' },
  { id: 'card_title', type: ELEMENT_TYPES.TEXT, x: 70, y: 40, width: 100, height: 20, text: 'TEACHER ID CARD', fontSize: 10, fontWeight: 'bold', color: '#374151', align: 'left' },
  { id: 'photo', type: ELEMENT_TYPES.PHOTO, x: 15, y: 70, width: 70, height: 85, locked: false },
  { id: 'name_label', type: ELEMENT_TYPES.TEXT, x: 95, y: 70, width: 50, height: 15, text: 'Name:', fontSize: 9, color: '#6b7280' },
  { id: 'name', type: ELEMENT_TYPES.TEXT, x: 145, y: 70, width: 120, height: 15, text: '{{name}}', fontSize: 10, fontWeight: 'bold', color: '#111827' },
  { id: 'emp_label', type: ELEMENT_TYPES.TEXT, x: 95, y: 88, width: 50, height: 15, text: 'Emp ID:', fontSize: 9, color: '#6b7280' },
  { id: 'emp_id', type: ELEMENT_TYPES.TEXT, x: 145, y: 88, width: 120, height: 15, text: '{{employee_id}}', fontSize: 10, color: '#111827' },
  { id: 'dept_label', type: ELEMENT_TYPES.TEXT, x: 95, y: 106, width: 50, height: 15, text: 'Dept:', fontSize: 9, color: '#6b7280' },
  { id: 'dept', type: ELEMENT_TYPES.TEXT, x: 145, y: 106, width: 120, height: 15, text: '{{department}}', fontSize: 10, color: '#111827' },
  { id: 'desig_label', type: ELEMENT_TYPES.TEXT, x: 95, y: 124, width: 50, height: 15, text: 'Desig:', fontSize: 9, color: '#6b7280' },
  { id: 'desig', type: ELEMENT_TYPES.TEXT, x: 145, y: 124, width: 120, height: 15, text: '{{designation}}', fontSize: 10, color: '#111827' },
  { id: 'blood_label', type: ELEMENT_TYPES.TEXT, x: 95, y: 142, width: 50, height: 15, text: 'Blood:', fontSize: 9, color: '#6b7280' },
  { id: 'blood', type: ELEMENT_TYPES.TEXT, x: 145, y: 142, width: 50, height: 15, text: '{{blood_group}}', fontSize: 10, fontWeight: 'bold', color: '#dc2626' },
  { id: 'qr', type: ELEMENT_TYPES.QR, x: 275, y: 70, width: 55, height: 55, locked: false },
  { id: 'join_label', type: ELEMENT_TYPES.TEXT, x: 270, y: 130, width: 60, height: 12, text: 'Since:', fontSize: 8, color: '#6b7280', align: 'center' },
  { id: 'join', type: ELEMENT_TYPES.TEXT, x: 270, y: 142, width: 60, height: 12, text: '{{joining_date}}', fontSize: 8, color: '#111827', align: 'center' },
  { id: 'footer_line', type: ELEMENT_TYPES.LINE, x: 10, y: 165, width: 320, height: 2, color: '#059669' },
  { id: 'phone', type: ELEMENT_TYPES.TEXT, x: 10, y: 175, width: 320, height: 15, text: 'Phone: {{phone}}', fontSize: 8, color: '#374151', align: 'center' },
  { id: 'address', type: ELEMENT_TYPES.TEXT, x: 10, y: 190, width: 320, height: 20, text: '{{school_address}}', fontSize: 7, color: '#6b7280', align: 'center' }
];

// Default teacher card elements - Portrait
const DEFAULT_TEACHER_PORTRAIT = [
  { id: 'header_bg', type: ELEMENT_TYPES.SHAPE, x: 0, y: 0, width: 215, height: 70, color: '#059669', fill: true },
  { id: 'logo', type: ELEMENT_TYPES.LOGO, x: 82, y: 8, width: 50, height: 50, locked: false },
  { id: 'school_name', type: ELEMENT_TYPES.TEXT, x: 10, y: 60, width: 195, height: 20, text: '{{school_name}}', fontSize: 10, fontWeight: 'bold', color: '#ffffff', align: 'center' },
  { id: 'card_title', type: ELEMENT_TYPES.TEXT, x: 10, y: 78, width: 195, height: 15, text: 'TEACHER IDENTITY CARD', fontSize: 9, fontWeight: 'bold', color: '#059669', align: 'center' },
  { id: 'photo', type: ELEMENT_TYPES.PHOTO, x: 57, y: 95, width: 100, height: 120, locked: false },
  { id: 'name', type: ELEMENT_TYPES.TEXT, x: 10, y: 220, width: 195, height: 18, text: '{{name}}', fontSize: 12, fontWeight: 'bold', color: '#111827', align: 'center' },
  { id: 'dept', type: ELEMENT_TYPES.TEXT, x: 10, y: 238, width: 195, height: 15, text: '{{department}}', fontSize: 10, color: '#374151', align: 'center' },
  { id: 'emp_id', type: ELEMENT_TYPES.TEXT, x: 10, y: 255, width: 195, height: 15, text: 'Emp ID: {{employee_id}}', fontSize: 10, color: '#374151', align: 'center' },
  { id: 'desig', type: ELEMENT_TYPES.TEXT, x: 10, y: 272, width: 100, height: 15, text: '{{designation}}', fontSize: 9, color: '#6b7280', align: 'left' },
  { id: 'blood', type: ELEMENT_TYPES.TEXT, x: 105, y: 272, width: 100, height: 15, text: 'Blood: {{blood_group}}', fontSize: 9, fontWeight: 'bold', color: '#dc2626', align: 'right' },
  { id: 'qr', type: ELEMENT_TYPES.QR, x: 77, y: 290, width: 60, height: 60, locked: false },
  { id: 'footer_line', type: ELEMENT_TYPES.LINE, x: 10, y: 355, width: 195, height: 2, color: '#059669' },
  { id: 'phone', type: ELEMENT_TYPES.TEXT, x: 10, y: 360, width: 195, height: 12, text: 'Phone: {{phone}}', fontSize: 7, color: '#374151', align: 'center' },
  { id: 'address', type: ELEMENT_TYPES.TEXT, x: 10, y: 372, width: 195, height: 20, text: '{{school_address}}', fontSize: 6, color: '#6b7280', align: 'center' }
];

// Default staff card elements - Landscape
const DEFAULT_STAFF_LANDSCAPE = [
  { id: 'logo', type: ELEMENT_TYPES.LOGO, x: 10, y: 10, width: 50, height: 50, locked: false },
  { id: 'school_name', type: ELEMENT_TYPES.TEXT, x: 70, y: 15, width: 200, height: 30, text: '{{school_name}}', fontSize: 14, fontWeight: 'bold', color: '#7c3aed', align: 'left' },
  { id: 'card_title', type: ELEMENT_TYPES.TEXT, x: 70, y: 40, width: 100, height: 20, text: 'STAFF ID CARD', fontSize: 10, fontWeight: 'bold', color: '#374151', align: 'left' },
  { id: 'photo', type: ELEMENT_TYPES.PHOTO, x: 15, y: 70, width: 70, height: 85, locked: false },
  { id: 'name_label', type: ELEMENT_TYPES.TEXT, x: 95, y: 70, width: 50, height: 15, text: 'Name:', fontSize: 9, color: '#6b7280' },
  { id: 'name', type: ELEMENT_TYPES.TEXT, x: 145, y: 70, width: 120, height: 15, text: '{{name}}', fontSize: 10, fontWeight: 'bold', color: '#111827' },
  { id: 'emp_label', type: ELEMENT_TYPES.TEXT, x: 95, y: 88, width: 50, height: 15, text: 'Emp ID:', fontSize: 9, color: '#6b7280' },
  { id: 'emp_id', type: ELEMENT_TYPES.TEXT, x: 145, y: 88, width: 120, height: 15, text: '{{employee_id}}', fontSize: 10, color: '#111827' },
  { id: 'dept_label', type: ELEMENT_TYPES.TEXT, x: 95, y: 106, width: 50, height: 15, text: 'Dept:', fontSize: 9, color: '#6b7280' },
  { id: 'dept', type: ELEMENT_TYPES.TEXT, x: 145, y: 106, width: 120, height: 15, text: '{{department}}', fontSize: 10, color: '#111827' },
  { id: 'desig_label', type: ELEMENT_TYPES.TEXT, x: 95, y: 124, width: 50, height: 15, text: 'Role:', fontSize: 9, color: '#6b7280' },
  { id: 'desig', type: ELEMENT_TYPES.TEXT, x: 145, y: 124, width: 120, height: 15, text: '{{designation}}', fontSize: 10, color: '#111827' },
  { id: 'blood_label', type: ELEMENT_TYPES.TEXT, x: 95, y: 142, width: 50, height: 15, text: 'Blood:', fontSize: 9, color: '#6b7280' },
  { id: 'blood', type: ELEMENT_TYPES.TEXT, x: 145, y: 142, width: 50, height: 15, text: '{{blood_group}}', fontSize: 10, fontWeight: 'bold', color: '#dc2626' },
  { id: 'qr', type: ELEMENT_TYPES.QR, x: 275, y: 70, width: 55, height: 55, locked: false },
  { id: 'join_label', type: ELEMENT_TYPES.TEXT, x: 270, y: 130, width: 60, height: 12, text: 'Since:', fontSize: 8, color: '#6b7280', align: 'center' },
  { id: 'join', type: ELEMENT_TYPES.TEXT, x: 270, y: 142, width: 60, height: 12, text: '{{joining_date}}', fontSize: 8, color: '#111827', align: 'center' },
  { id: 'footer_line', type: ELEMENT_TYPES.LINE, x: 10, y: 165, width: 320, height: 2, color: '#7c3aed' },
  { id: 'phone', type: ELEMENT_TYPES.TEXT, x: 10, y: 175, width: 320, height: 15, text: 'Phone: {{phone}}', fontSize: 8, color: '#374151', align: 'center' },
  { id: 'address', type: ELEMENT_TYPES.TEXT, x: 10, y: 190, width: 320, height: 20, text: '{{school_address}}', fontSize: 7, color: '#6b7280', align: 'center' }
];

// Default staff card elements - Portrait
const DEFAULT_STAFF_PORTRAIT = [
  { id: 'header_bg', type: ELEMENT_TYPES.SHAPE, x: 0, y: 0, width: 215, height: 70, color: '#7c3aed', fill: true },
  { id: 'logo', type: ELEMENT_TYPES.LOGO, x: 82, y: 8, width: 50, height: 50, locked: false },
  { id: 'school_name', type: ELEMENT_TYPES.TEXT, x: 10, y: 60, width: 195, height: 20, text: '{{school_name}}', fontSize: 10, fontWeight: 'bold', color: '#ffffff', align: 'center' },
  { id: 'card_title', type: ELEMENT_TYPES.TEXT, x: 10, y: 78, width: 195, height: 15, text: 'STAFF IDENTITY CARD', fontSize: 9, fontWeight: 'bold', color: '#7c3aed', align: 'center' },
  { id: 'photo', type: ELEMENT_TYPES.PHOTO, x: 57, y: 95, width: 100, height: 120, locked: false },
  { id: 'name', type: ELEMENT_TYPES.TEXT, x: 10, y: 220, width: 195, height: 18, text: '{{name}}', fontSize: 12, fontWeight: 'bold', color: '#111827', align: 'center' },
  { id: 'dept', type: ELEMENT_TYPES.TEXT, x: 10, y: 238, width: 195, height: 15, text: '{{department}}', fontSize: 10, color: '#374151', align: 'center' },
  { id: 'emp_id', type: ELEMENT_TYPES.TEXT, x: 10, y: 255, width: 195, height: 15, text: 'Emp ID: {{employee_id}}', fontSize: 10, color: '#374151', align: 'center' },
  { id: 'desig', type: ELEMENT_TYPES.TEXT, x: 10, y: 272, width: 100, height: 15, text: '{{designation}}', fontSize: 9, color: '#6b7280', align: 'left' },
  { id: 'blood', type: ELEMENT_TYPES.TEXT, x: 105, y: 272, width: 100, height: 15, text: 'Blood: {{blood_group}}', fontSize: 9, fontWeight: 'bold', color: '#dc2626', align: 'right' },
  { id: 'qr', type: ELEMENT_TYPES.QR, x: 77, y: 290, width: 60, height: 60, locked: false },
  { id: 'footer_line', type: ELEMENT_TYPES.LINE, x: 10, y: 355, width: 195, height: 2, color: '#7c3aed' },
  { id: 'phone', type: ELEMENT_TYPES.TEXT, x: 10, y: 360, width: 195, height: 12, text: 'Phone: {{phone}}', fontSize: 7, color: '#374151', align: 'center' },
  { id: 'address', type: ELEMENT_TYPES.TEXT, x: 10, y: 372, width: 195, height: 20, text: '{{school_address}}', fontSize: 6, color: '#6b7280', align: 'center' }
];

const getDefaultElements = (cardType, orientation) => {
  if (cardType === CARD_TYPES.STUDENT) {
    return orientation === 'portrait' ? DEFAULT_STUDENT_PORTRAIT : DEFAULT_STUDENT_LANDSCAPE;
  } else if (cardType === CARD_TYPES.TEACHER) {
    return orientation === 'portrait' ? DEFAULT_TEACHER_PORTRAIT : DEFAULT_TEACHER_LANDSCAPE;
  } else {
    return orientation === 'portrait' ? DEFAULT_STAFF_PORTRAIT : DEFAULT_STAFF_LANDSCAPE;
  }
};

const IdCardDesignerPage = () => {
  const navigate = useNavigate();
  const canvasRef = useRef(null);
  const bgInputRef = useRef(null);
  
  // Card settings
  const [cardType, setCardType] = useState(CARD_TYPES.STUDENT);
  const [orientation, setOrientation] = useState('landscape');
  const cardDimensions = orientation === 'portrait' ? ORIENTATIONS.PORTRAIT : ORIENTATIONS.LANDSCAPE;
  
  // Elements
  const [elements, setElements] = useState(DEFAULT_STUDENT_LANDSCAPE);
  const [selectedElement, setSelectedElement] = useState(null);
  
  // Drag state
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  
  // Card styling
  const [cardBg, setCardBg] = useState('#ffffff');
  const [cardBorder, setCardBorder] = useState('#e5e7eb');
  const [headerColor, setHeaderColor] = useState('#1e40af');
  const [backgroundImage, setBackgroundImage] = useState(null);
  const [bgOpacity, setBgOpacity] = useState(0.3);
  
  // UI state
  const [showGrid, setShowGrid] = useState(true);
  const [templates, setTemplates] = useState([]);
  const [templateName, setTemplateName] = useState('');
  const [saveDialog, setSaveDialog] = useState(false);
  
  // Preview data
  const [previewData, setPreviewData] = useState(null);
  const [students, setStudents] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [staff, setStaff] = useState([]);
  const [school, setSchool] = useState(null);

  // Fetch initial data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [studentsRes, teachersRes, staffRes, schoolRes, templatesRes] = await Promise.all([
          axios.get(`${API_URL}/api/students?page_size=50`, { headers: getAuthHeader() }).catch(() => ({ data: { items: [] } })),
          axios.get(`${API_URL}/api/teachers?page_size=50`, { headers: getAuthHeader() }).catch(() => ({ data: [] })),
          axios.get(`${API_URL}/api/staff?page_size=50`, { headers: getAuthHeader() }).catch(() => ({ data: [] })),
          axios.get(`${API_URL}/api/schools/current`, { headers: getAuthHeader() }),
          axios.get(`${API_URL}/api/id-cards/templates`, { headers: getAuthHeader() }).catch(() => ({ data: [] }))
        ]);
        
        const studentList = studentsRes.data?.items || [];
        const teacherList = Array.isArray(teachersRes.data) ? teachersRes.data : (teachersRes.data?.items || []);
        const staffList = Array.isArray(staffRes.data) ? staffRes.data : (staffRes.data?.items || []);
        
        setStudents(studentList);
        setTeachers(teacherList);
        setStaff(staffList);
        setSchool(schoolRes.data);
        setTemplates(templatesRes.data || []);
        
        if (studentList[0]) {
          setPreviewData(studentList[0]);
        }
      } catch (error) {
        console.error('Failed to fetch data:', error);
      }
    };
    fetchData();
  }, []);

  // Handle card type change
  const handleCardTypeChange = (newType) => {
    setCardType(newType);
    setElements(getDefaultElements(newType, orientation));
    setSelectedElement(null);
    
    // Set header color based on type
    if (newType === CARD_TYPES.STUDENT) {
      setHeaderColor('#1e40af');
      if (students[0]) setPreviewData(students[0]);
    } else if (newType === CARD_TYPES.TEACHER) {
      setHeaderColor('#059669');
      if (teachers[0]) setPreviewData(teachers[0]);
    } else {
      setHeaderColor('#7c3aed');
      if (staff[0]) setPreviewData(staff[0]);
    }
  };

  // Handle orientation change
  const handleOrientationChange = (newOrientation) => {
    setOrientation(newOrientation);
    setElements(getDefaultElements(cardType, newOrientation));
    setSelectedElement(null);
  };

  // Handle background image upload
  const handleBgImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setBackgroundImage(event.target?.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Handle element selection
  const handleElementClick = (e, element) => {
    e.stopPropagation();
    if (!element.locked) {
      setSelectedElement(element);
    }
  };

  // Handle canvas click (deselect)
  const handleCanvasClick = () => {
    setSelectedElement(null);
  };

  // Handle drag start
  const handleDragStart = (e, element) => {
    if (element.locked) return;
    e.stopPropagation();
    setIsDragging(true);
    setSelectedElement(element);
    const rect = e.currentTarget.getBoundingClientRect();
    setDragOffset({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    });
  };

  // Handle drag move
  const handleDragMove = useCallback((e) => {
    if (!isDragging || !selectedElement || !canvasRef.current) return;
    
    const canvasRect = canvasRef.current.getBoundingClientRect();
    let newX = e.clientX - canvasRect.left - dragOffset.x;
    let newY = e.clientY - canvasRect.top - dragOffset.y;
    
    // Snap to grid
    if (showGrid) {
      newX = Math.round(newX / 5) * 5;
      newY = Math.round(newY / 5) * 5;
    }
    
    // Keep within bounds
    newX = Math.max(0, Math.min(newX, cardDimensions.width - selectedElement.width));
    newY = Math.max(0, Math.min(newY, cardDimensions.height - selectedElement.height));
    
    setElements(prev => prev.map(el => 
      el.id === selectedElement.id ? { ...el, x: newX, y: newY } : el
    ));
  }, [isDragging, selectedElement, dragOffset, showGrid, cardDimensions]);

  // Handle drag end
  const handleDragEnd = () => {
    setIsDragging(false);
  };

  // Add mouse event listeners
  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleDragMove);
      window.addEventListener('mouseup', handleDragEnd);
      return () => {
        window.removeEventListener('mousemove', handleDragMove);
        window.removeEventListener('mouseup', handleDragEnd);
      };
    }
  }, [isDragging, handleDragMove]);

  // Update element property
  const updateElement = (property, value) => {
    if (!selectedElement) return;
    setElements(prev => prev.map(el => 
      el.id === selectedElement.id ? { ...el, [property]: value } : el
    ));
    setSelectedElement(prev => ({ ...prev, [property]: value }));
  };

  // Add new element
  const addElement = (type) => {
    const newElement = {
      id: `${type}_${Date.now()}`,
      type,
      x: 50,
      y: 50,
      width: type === ELEMENT_TYPES.LINE ? 100 : 60,
      height: type === ELEMENT_TYPES.LINE ? 2 : 60,
      ...(type === ELEMENT_TYPES.TEXT && { text: 'New Text', fontSize: 12, color: '#000000', fontWeight: 'normal', align: 'left' }),
      ...(type === ELEMENT_TYPES.SHAPE && { shape: 'rect', color: '#3b82f6', fill: true }),
      ...(type === ELEMENT_TYPES.LINE && { color: '#000000' }),
      ...(type === ELEMENT_TYPES.IMAGE && { imageUrl: null }),
      locked: false
    };
    setElements(prev => [...prev, newElement]);
    setSelectedElement(newElement);
  };

  // Delete element
  const deleteElement = () => {
    if (!selectedElement) return;
    setElements(prev => prev.filter(el => el.id !== selectedElement.id));
    setSelectedElement(null);
  };

  // Move element layer
  const moveLayer = (direction) => {
    if (!selectedElement) return;
    const index = elements.findIndex(el => el.id === selectedElement.id);
    const newElements = [...elements];
    
    if (direction === 'up' && index < elements.length - 1) {
      [newElements[index], newElements[index + 1]] = [newElements[index + 1], newElements[index]];
    } else if (direction === 'down' && index > 0) {
      [newElements[index], newElements[index - 1]] = [newElements[index - 1], newElements[index]];
    }
    setElements(newElements);
  };

  // Replace template variable with actual value
  const replaceVariables = (text) => {
    if (!previewData || !school) return text;
    
    const vars = {
      '{{school_name}}': school.name || 'School Name',
      '{{school_address}}': school.address || 'School Address',
      '{{name}}': previewData.name || 'Name',
      '{{class}}': previewData.class_name || '',
      '{{section}}': previewData.section || '',
      '{{student_id}}': previewData.student_id || previewData.admission_number || '',
      '{{employee_id}}': previewData.employee_id || '',
      '{{department}}': previewData.department || previewData.subject || '',
      '{{designation}}': previewData.designation || previewData.role || '',
      '{{dob}}': previewData.date_of_birth || '',
      '{{blood_group}}': previewData.blood_group || 'N/A',
      '{{emergency_contact}}': previewData.emergency_contact || previewData.father_phone || previewData.phone || '',
      '{{father_name}}': previewData.father_name || '',
      '{{mother_name}}': previewData.mother_name || '',
      '{{valid_till}}': '31-Mar-2027',
      '{{admission_date}}': previewData.admission_date || '',
      '{{joining_date}}': previewData.joining_date || '',
      '{{phone}}': previewData.phone || previewData.mobile || ''
    };
    
    let result = text;
    Object.entries(vars).forEach(([key, value]) => {
      result = result.replace(new RegExp(key.replace(/[{}]/g, '\\$&'), 'g'), value);
    });
    return result;
  };

  // Save template
  const saveTemplate = async () => {
    if (!templateName.trim()) {
      toast.error('Please enter a template name');
      return;
    }
    
    try {
      await axios.post(`${API_URL}/api/id-cards/templates`, {
        name: templateName,
        elements: elements,
        cardBg,
        cardBorder,
        headerColor,
        backgroundImage,
        bgOpacity,
        orientation,
        card_type: cardType
      }, { headers: getAuthHeader() });
      
      toast.success('Template saved!');
      setSaveDialog(false);
      setTemplateName('');
    } catch (error) {
      toast.error('Failed to save template');
    }
  };

  // Load template
  const loadTemplate = (template) => {
    setElements(template.elements || []);
    setCardBg(template.cardBg || '#ffffff');
    setCardBorder(template.cardBorder || '#e5e7eb');
    setHeaderColor(template.headerColor || '#1e40af');
    setBackgroundImage(template.backgroundImage || null);
    setBgOpacity(template.bgOpacity || 0.3);
    setOrientation(template.orientation || 'landscape');
    setCardType(template.card_type || CARD_TYPES.STUDENT);
    toast.success(`Loaded: ${template.name}`);
  };

  // Get preview list based on card type
  const getPreviewList = () => {
    if (cardType === CARD_TYPES.STUDENT) return students;
    if (cardType === CARD_TYPES.TEACHER) return teachers;
    return staff;
  };

  // Get quick variables based on card type
  const getQuickVariables = () => {
    if (cardType === CARD_TYPES.STUDENT) {
      return ['{{name}}', '{{class}}', '{{student_id}}', '{{dob}}', '{{blood_group}}', '{{father_name}}'];
    }
    return ['{{name}}', '{{employee_id}}', '{{department}}', '{{designation}}', '{{blood_group}}', '{{phone}}'];
  };

  // Render element on canvas
  const renderElement = (element) => {
    const isSelected = selectedElement?.id === element.id;
    const baseStyle = {
      position: 'absolute',
      left: element.x,
      top: element.y,
      width: element.width,
      height: element.height,
      cursor: element.locked ? 'not-allowed' : 'move',
      border: isSelected ? '2px solid #3b82f6' : '1px dashed transparent',
      borderRadius: 2,
      boxSizing: 'border-box',
      userSelect: 'none'
    };

    switch (element.type) {
      case ELEMENT_TYPES.TEXT:
        return (
          <div
            key={element.id}
            style={{
              ...baseStyle,
              fontSize: element.fontSize,
              fontWeight: element.fontWeight,
              fontStyle: element.fontStyle,
              textDecoration: element.textDecoration,
              color: element.color,
              textAlign: element.align,
              overflow: 'hidden',
              whiteSpace: 'nowrap',
              display: 'flex',
              alignItems: 'center',
              justifyContent: element.align === 'center' ? 'center' : element.align === 'right' ? 'flex-end' : 'flex-start'
            }}
            onClick={(e) => handleElementClick(e, element)}
            onMouseDown={(e) => handleDragStart(e, element)}
          >
            {replaceVariables(element.text)}
          </div>
        );

      case ELEMENT_TYPES.LOGO:
        return (
          <div
            key={element.id}
            style={{
              ...baseStyle,
              background: school?.logo_url ? `url(${school.logo_url}) center/contain no-repeat` : '#e5e7eb',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            onClick={(e) => handleElementClick(e, element)}
            onMouseDown={(e) => handleDragStart(e, element)}
          >
            {!school?.logo_url && <Image className="h-6 w-6 text-gray-400" />}
          </div>
        );

      case ELEMENT_TYPES.PHOTO:
        return (
          <div
            key={element.id}
            style={{
              ...baseStyle,
              background: previewData?.photo_url ? `url(${previewData.photo_url}) center/cover` : '#f3f4f6',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: 4
            }}
            onClick={(e) => handleElementClick(e, element)}
            onMouseDown={(e) => handleDragStart(e, element)}
          >
            {!previewData?.photo_url && <User className="h-8 w-8 text-gray-400" />}
          </div>
        );

      case ELEMENT_TYPES.QR:
        return (
          <div
            key={element.id}
            style={{
              ...baseStyle,
              background: '#f9fafb',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: isSelected ? '2px solid #3b82f6' : '1px solid #e5e7eb'
            }}
            onClick={(e) => handleElementClick(e, element)}
            onMouseDown={(e) => handleDragStart(e, element)}
          >
            <QrCode className="h-full w-full p-1 text-gray-700" />
          </div>
        );

      case ELEMENT_TYPES.LINE:
        return (
          <div
            key={element.id}
            style={{
              ...baseStyle,
              background: element.color,
              height: element.height || 2
            }}
            onClick={(e) => handleElementClick(e, element)}
            onMouseDown={(e) => handleDragStart(e, element)}
          />
        );

      case ELEMENT_TYPES.SHAPE:
        return (
          <div
            key={element.id}
            style={{
              ...baseStyle,
              background: element.fill ? element.color : 'transparent',
              border: element.fill ? 'none' : `2px solid ${element.color}`,
              borderRadius: element.shape === 'circle' ? '50%' : 0
            }}
            onClick={(e) => handleElementClick(e, element)}
            onMouseDown={(e) => handleDragStart(e, element)}
          />
        );

      case ELEMENT_TYPES.IMAGE:
        return (
          <div
            key={element.id}
            style={{
              ...baseStyle,
              background: element.imageUrl ? `url(${element.imageUrl}) center/contain no-repeat` : '#e5e7eb',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            onClick={(e) => handleElementClick(e, element)}
            onMouseDown={(e) => handleDragStart(e, element)}
          >
            {!element.imageUrl && <ImagePlus className="h-6 w-6 text-gray-400" />}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="p-4 md:p-6 space-y-4" data-testid="id-card-designer-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="back-btn">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">ID Card Designer</h1>
            <p className="text-muted-foreground">Design custom ID cards for students, teachers, and staff</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setSaveDialog(true)} data-testid="save-template-btn">
            <Save className="h-4 w-4 mr-1" /> Save Template
          </Button>
          <Button onClick={() => navigate('/id-cards')} data-testid="preview-print-btn">
            <Eye className="h-4 w-4 mr-1" /> Preview & Print
          </Button>
        </div>
      </div>

      {/* Card Type & Orientation Selection */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-6">
            {/* Card Type */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Card Type</Label>
              <div className="flex gap-2">
                <Button 
                  variant={cardType === CARD_TYPES.STUDENT ? 'default' : 'outline'} 
                  size="sm"
                  onClick={() => handleCardTypeChange(CARD_TYPES.STUDENT)}
                  data-testid="card-type-student"
                >
                  <GraduationCap className="h-4 w-4 mr-1" /> Student
                </Button>
                <Button 
                  variant={cardType === CARD_TYPES.TEACHER ? 'default' : 'outline'} 
                  size="sm"
                  onClick={() => handleCardTypeChange(CARD_TYPES.TEACHER)}
                  data-testid="card-type-teacher"
                >
                  <User className="h-4 w-4 mr-1" /> Teacher
                </Button>
                <Button 
                  variant={cardType === CARD_TYPES.STAFF ? 'default' : 'outline'} 
                  size="sm"
                  onClick={() => handleCardTypeChange(CARD_TYPES.STAFF)}
                  data-testid="card-type-staff"
                >
                  <Briefcase className="h-4 w-4 mr-1" /> Staff
                </Button>
              </div>
            </div>

            {/* Orientation */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Orientation</Label>
              <div className="flex gap-2">
                <Button 
                  variant={orientation === 'landscape' ? 'default' : 'outline'} 
                  size="sm"
                  onClick={() => handleOrientationChange('landscape')}
                  data-testid="orientation-landscape"
                >
                  <Square className="h-4 w-4 mr-1 rotate-90" /> Landscape
                </Button>
                <Button 
                  variant={orientation === 'portrait' ? 'default' : 'outline'} 
                  size="sm"
                  onClick={() => handleOrientationChange('portrait')}
                  data-testid="orientation-portrait"
                >
                  <Square className="h-4 w-4 mr-1" /> Portrait
                </Button>
              </div>
            </div>

            {/* Saved Templates */}
            {templates.length > 0 && (
              <div className="space-y-2">
                <Label className="text-sm font-medium">Load Template</Label>
                <Select onValueChange={(v) => {
                  const t = templates.find(t => t.id === v);
                  if (t) loadTemplate(t);
                }}>
                  <SelectTrigger className="w-[180px]" data-testid="load-template-select">
                    <SelectValue placeholder="Select template" />
                  </SelectTrigger>
                  <SelectContent>
                    {templates.map(t => (
                      <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        {/* Left Panel - Elements & Colors */}
        <Card className="lg:col-span-1">
          <Tabs defaultValue="elements">
            <TabsList className="w-full">
              <TabsTrigger value="elements" className="flex-1">Elements</TabsTrigger>
              <TabsTrigger value="colors" className="flex-1">Colors</TabsTrigger>
            </TabsList>
            
            <TabsContent value="elements">
              <CardContent className="space-y-3 pt-4">
                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">Add Elements</Label>
                  <Button variant="outline" className="w-full justify-start" onClick={() => addElement(ELEMENT_TYPES.TEXT)} data-testid="add-text-btn">
                    <Type className="h-4 w-4 mr-2" /> Text
                  </Button>
                  <Button variant="outline" className="w-full justify-start" onClick={() => addElement(ELEMENT_TYPES.LINE)} data-testid="add-line-btn">
                    <Minus className="h-4 w-4 mr-2" /> Line
                  </Button>
                  <Button variant="outline" className="w-full justify-start" onClick={() => addElement(ELEMENT_TYPES.SHAPE)} data-testid="add-shape-btn">
                    <Square className="h-4 w-4 mr-2" /> Shape
                  </Button>
                  <Button variant="outline" className="w-full justify-start" onClick={() => addElement(ELEMENT_TYPES.IMAGE)} data-testid="add-image-btn">
                    <ImagePlus className="h-4 w-4 mr-2" /> Image
                  </Button>
                </div>
                
                <div className="pt-2 border-t">
                  <Label className="text-xs text-muted-foreground">Quick Variables</Label>
                  <div className="flex flex-wrap gap-1 mt-2">
                    {getQuickVariables().map(v => (
                      <button
                        key={v}
                        className="text-xs px-2 py-1 bg-muted rounded hover:bg-muted/80"
                        onClick={() => {
                          if (selectedElement?.type === ELEMENT_TYPES.TEXT) {
                            updateElement('text', selectedElement.text + ' ' + v);
                          }
                        }}
                      >
                        {v.replace(/[{}]/g, '')}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="pt-2 border-t">
                  <Label className="text-xs">Preview Person</Label>
                  <Select 
                    value={previewData?.id || ''} 
                    onValueChange={(v) => {
                      const list = getPreviewList();
                      setPreviewData(list.find(item => item.id === v));
                    }}
                  >
                    <SelectTrigger className="mt-1" data-testid="preview-person-select">
                      <SelectValue placeholder="Select person" />
                    </SelectTrigger>
                    <SelectContent>
                      {getPreviewList().map(item => (
                        <SelectItem key={item.id} value={item.id}>{item.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </TabsContent>
            
            <TabsContent value="colors">
              <CardContent className="space-y-4 pt-4">
                {/* Background Color */}
                <div>
                  <Label className="text-xs">Background Color</Label>
                  <div className="flex gap-2 mt-1">
                    <Input 
                      type="color" 
                      value={cardBg} 
                      onChange={(e) => setCardBg(e.target.value)}
                      className="h-10 w-14 p-1 cursor-pointer"
                      data-testid="bg-color-input"
                    />
                    <Input 
                      value={cardBg} 
                      onChange={(e) => setCardBg(e.target.value)}
                      className="flex-1"
                    />
                  </div>
                </div>

                {/* Header Color */}
                <div>
                  <Label className="text-xs">Header Color</Label>
                  <div className="flex gap-2 mt-1">
                    <Input 
                      type="color" 
                      value={headerColor} 
                      onChange={(e) => setHeaderColor(e.target.value)}
                      className="h-10 w-14 p-1 cursor-pointer"
                      data-testid="header-color-input"
                    />
                    <Input 
                      value={headerColor} 
                      onChange={(e) => setHeaderColor(e.target.value)}
                      className="flex-1"
                    />
                  </div>
                </div>

                {/* Border Color */}
                <div>
                  <Label className="text-xs">Border Color</Label>
                  <div className="flex gap-2 mt-1">
                    <Input 
                      type="color" 
                      value={cardBorder} 
                      onChange={(e) => setCardBorder(e.target.value)}
                      className="h-10 w-14 p-1 cursor-pointer"
                      data-testid="border-color-input"
                    />
                    <Input 
                      value={cardBorder} 
                      onChange={(e) => setCardBorder(e.target.value)}
                      className="flex-1"
                    />
                  </div>
                </div>

                {/* Background Image */}
                <div className="pt-2 border-t">
                  <Label className="text-xs">Background Image</Label>
                  <input 
                    type="file" 
                    ref={bgInputRef}
                    accept="image/*"
                    onChange={handleBgImageUpload}
                    className="hidden"
                  />
                  <div className="flex gap-2 mt-1">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="flex-1"
                      onClick={() => bgInputRef.current?.click()}
                      data-testid="upload-bg-image-btn"
                    >
                      <Upload className="h-4 w-4 mr-1" /> Upload
                    </Button>
                    {backgroundImage && (
                      <Button 
                        variant="destructive" 
                        size="sm"
                        onClick={() => setBackgroundImage(null)}
                        data-testid="remove-bg-image-btn"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                  {backgroundImage && (
                    <div className="mt-2">
                      <Label className="text-xs">Opacity: {Math.round(bgOpacity * 100)}%</Label>
                      <input 
                        type="range" 
                        min="0" 
                        max="100" 
                        value={bgOpacity * 100}
                        onChange={(e) => setBgOpacity(parseInt(e.target.value) / 100)}
                        className="w-full mt-1"
                        data-testid="bg-opacity-slider"
                      />
                    </div>
                  )}
                </div>

                {/* Quick Color Presets */}
                <div className="pt-2 border-t">
                  <Label className="text-xs">Quick Presets</Label>
                  <div className="grid grid-cols-5 gap-1 mt-2">
                    {['#1e40af', '#059669', '#7c3aed', '#dc2626', '#f59e0b', '#0891b2', '#4f46e5', '#be185d', '#16a34a', '#0d9488'].map(color => (
                      <button
                        key={color}
                        className="h-8 w-full rounded border-2 border-transparent hover:border-gray-400"
                        style={{ background: color }}
                        onClick={() => setHeaderColor(color)}
                      />
                    ))}
                  </div>
                </div>
              </CardContent>
            </TabsContent>
          </Tabs>
        </Card>

        {/* Center - Canvas */}
        <Card className="lg:col-span-2">
          <CardHeader className="pb-2 flex flex-row items-center justify-between">
            <CardTitle className="text-sm">Design Canvas ({cardDimensions.width} x {cardDimensions.height})</CardTitle>
            <div className="flex items-center gap-2">
              <Label className="text-xs">Grid</Label>
              <Switch checked={showGrid} onCheckedChange={setShowGrid} data-testid="grid-toggle" />
            </div>
          </CardHeader>
          <CardContent className="flex justify-center overflow-auto p-4">
            <div
              ref={canvasRef}
              className="relative border-2 rounded-lg overflow-hidden shadow-lg"
              style={{
                width: cardDimensions.width,
                height: cardDimensions.height,
                minWidth: cardDimensions.width,
                minHeight: cardDimensions.height,
                background: cardBg,
                borderColor: cardBorder,
                backgroundImage: backgroundImage 
                  ? `linear-gradient(rgba(255,255,255,${1-bgOpacity}), rgba(255,255,255,${1-bgOpacity})), url(${backgroundImage})` 
                  : showGrid 
                    ? 'linear-gradient(#f0f0f0 1px, transparent 1px), linear-gradient(90deg, #f0f0f0 1px, transparent 1px)' 
                    : 'none',
                backgroundSize: backgroundImage ? 'cover' : '10px 10px',
                backgroundPosition: 'center'
              }}
              onClick={handleCanvasClick}
              data-testid="design-canvas"
            >
              {elements.map(renderElement)}
            </div>
          </CardContent>
        </Card>

        {/* Right Panel - Properties */}
        <Card className="lg:col-span-1">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Properties</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {selectedElement ? (
              <>
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium uppercase bg-muted px-2 py-1 rounded">{selectedElement.type}</span>
                  <div className="flex gap-1">
                    <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => updateElement('locked', !selectedElement.locked)}>
                      {selectedElement.locked ? <Lock className="h-3 w-3" /> : <Unlock className="h-3 w-3" />}
                    </Button>
                    <Button size="icon" variant="ghost" className="h-6 w-6" onClick={deleteElement} data-testid="delete-element-btn">
                      <Trash2 className="h-3 w-3 text-red-500" />
                    </Button>
                  </div>
                </div>

                {/* Position */}
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs">X</Label>
                    <Input 
                      type="number" 
                      value={selectedElement.x} 
                      onChange={(e) => updateElement('x', parseInt(e.target.value) || 0)}
                      className="h-8"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Y</Label>
                    <Input 
                      type="number" 
                      value={selectedElement.y} 
                      onChange={(e) => updateElement('y', parseInt(e.target.value) || 0)}
                      className="h-8"
                    />
                  </div>
                </div>

                {/* Size */}
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs">Width</Label>
                    <Input 
                      type="number" 
                      value={selectedElement.width} 
                      onChange={(e) => updateElement('width', parseInt(e.target.value) || 10)}
                      className="h-8"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Height</Label>
                    <Input 
                      type="number" 
                      value={selectedElement.height} 
                      onChange={(e) => updateElement('height', parseInt(e.target.value) || 10)}
                      className="h-8"
                    />
                  </div>
                </div>

                {/* Text properties */}
                {selectedElement.type === ELEMENT_TYPES.TEXT && (
                  <>
                    <div>
                      <Label className="text-xs">Text</Label>
                      <Input 
                        value={selectedElement.text} 
                        onChange={(e) => updateElement('text', e.target.value)}
                        className="h-8"
                        data-testid="text-content-input"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-xs">Font Size</Label>
                        <Input 
                          type="number" 
                          value={selectedElement.fontSize} 
                          onChange={(e) => updateElement('fontSize', parseInt(e.target.value) || 12)}
                          className="h-8"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Color</Label>
                        <Input 
                          type="color" 
                          value={selectedElement.color} 
                          onChange={(e) => updateElement('color', e.target.value)}
                          className="h-8"
                        />
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <Button 
                        size="icon" 
                        variant={selectedElement.fontWeight === 'bold' ? 'default' : 'outline'} 
                        className="h-8 w-8"
                        onClick={() => updateElement('fontWeight', selectedElement.fontWeight === 'bold' ? 'normal' : 'bold')}
                      >
                        <Bold className="h-3 w-3" />
                      </Button>
                      <Button 
                        size="icon" 
                        variant={selectedElement.fontStyle === 'italic' ? 'default' : 'outline'} 
                        className="h-8 w-8"
                        onClick={() => updateElement('fontStyle', selectedElement.fontStyle === 'italic' ? 'normal' : 'italic')}
                      >
                        <Italic className="h-3 w-3" />
                      </Button>
                      <Button 
                        size="icon" 
                        variant={selectedElement.align === 'left' ? 'default' : 'outline'} 
                        className="h-8 w-8"
                        onClick={() => updateElement('align', 'left')}
                      >
                        <AlignLeft className="h-3 w-3" />
                      </Button>
                      <Button 
                        size="icon" 
                        variant={selectedElement.align === 'center' ? 'default' : 'outline'} 
                        className="h-8 w-8"
                        onClick={() => updateElement('align', 'center')}
                      >
                        <AlignCenter className="h-3 w-3" />
                      </Button>
                      <Button 
                        size="icon" 
                        variant={selectedElement.align === 'right' ? 'default' : 'outline'} 
                        className="h-8 w-8"
                        onClick={() => updateElement('align', 'right')}
                      >
                        <AlignRight className="h-3 w-3" />
                      </Button>
                    </div>
                  </>
                )}

                {/* Shape properties */}
                {selectedElement.type === ELEMENT_TYPES.SHAPE && (
                  <>
                    <div>
                      <Label className="text-xs">Shape</Label>
                      <Select value={selectedElement.shape || 'rect'} onValueChange={(v) => updateElement('shape', v)}>
                        <SelectTrigger className="h-8">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="rect">Rectangle</SelectItem>
                          <SelectItem value="circle">Circle</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="flex-1">
                        <Label className="text-xs">Color</Label>
                        <Input 
                          type="color" 
                          value={selectedElement.color} 
                          onChange={(e) => updateElement('color', e.target.value)}
                          className="h-8"
                        />
                      </div>
                      <div className="flex items-center gap-2 pt-4">
                        <Label className="text-xs">Fill</Label>
                        <Switch 
                          checked={selectedElement.fill} 
                          onCheckedChange={(v) => updateElement('fill', v)}
                        />
                      </div>
                    </div>
                  </>
                )}

                {/* Line/Shape color */}
                {selectedElement.type === ELEMENT_TYPES.LINE && (
                  <div>
                    <Label className="text-xs">Color</Label>
                    <Input 
                      type="color" 
                      value={selectedElement.color} 
                      onChange={(e) => updateElement('color', e.target.value)}
                      className="h-8"
                    />
                  </div>
                )}

                {/* Image URL for custom images */}
                {selectedElement.type === ELEMENT_TYPES.IMAGE && (
                  <div>
                    <Label className="text-xs">Image URL</Label>
                    <Input 
                      value={selectedElement.imageUrl || ''} 
                      onChange={(e) => updateElement('imageUrl', e.target.value)}
                      placeholder="Paste image URL"
                      className="h-8"
                    />
                  </div>
                )}

                {/* Layer controls */}
                <div className="flex gap-1 pt-2 border-t">
                  <Button size="sm" variant="outline" className="flex-1" onClick={() => moveLayer('up')}>
                    <ChevronUp className="h-3 w-3 mr-1" /> Up
                  </Button>
                  <Button size="sm" variant="outline" className="flex-1" onClick={() => moveLayer('down')}>
                    <ChevronDown className="h-3 w-3 mr-1" /> Down
                  </Button>
                </div>
              </>
            ) : (
              <div className="text-center text-muted-foreground py-8">
                <Layers className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">Select an element to edit</p>
              </div>
            )}

            {/* Reset */}
            <div className="pt-2 border-t">
              <Button variant="outline" className="w-full" onClick={() => setElements(getDefaultElements(cardType, orientation))} data-testid="reset-default-btn">
                <RotateCcw className="h-4 w-4 mr-1" /> Reset to Default
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Save Dialog */}
      <Dialog open={saveDialog} onOpenChange={setSaveDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Save Template</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Template Name</Label>
              <Input 
                value={templateName} 
                onChange={(e) => setTemplateName(e.target.value)}
                placeholder="My Custom Template"
                data-testid="template-name-input"
              />
            </div>
            <div className="text-sm text-muted-foreground">
              <p>This will save:</p>
              <ul className="list-disc list-inside mt-1">
                <li>Card type: {cardType}</li>
                <li>Orientation: {orientation}</li>
                <li>All colors and background image</li>
                <li>All element positions</li>
              </ul>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSaveDialog(false)}>Cancel</Button>
            <Button onClick={saveTemplate} data-testid="confirm-save-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default IdCardDesignerPage;
